import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClients } from '../store/clientsSlice';
import './DeliveryTradesPage.css';

const apiUrl = process.env.REACT_APP_API_URL;

const fetchDeliveryTradesRequest = (clientId, financialYear) => axios.get(`${apiUrl}/api/trades/delivery`, {
  params: {
    clientId,
    financialYear,
  },
});

const formatAmount = (value) => {
  const number = Number(value ?? 0);
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(new Date(value));
};

const calculateNetAmount = (trade) => {
  if (trade.netProfitLoss != null) {
    return Number(trade.netProfitLoss);
  }
  return Number(trade.sellValue ?? 0) - Number(trade.buyValue ?? 0) - Number(trade.chargesAndStatutoryLevies ?? 0);
};

const DeliveryTradesPage = () => {
  const dispatch = useDispatch();
  const { clientId, financialYear } = useSelector((state) => state.app);
  const [trades, setTrades] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [fmvDrafts, setFmvDrafts] = useState({});
  const [selectedTradeIds, setSelectedTradeIds] = useState([]);
  const [bulkClassification, setBulkClassification] = useState('INVESTMENT');
  const [loading, setLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [savingTradeId, setSavingTradeId] = useState('');
  const [error, setError] = useState('');

  const applyTrades = (items) => {
    setTrades(items);
    setDrafts(Object.fromEntries(items.map((trade) => [trade.id, trade.tradeClassification || 'INVESTMENT'])));
    setFmvDrafts(Object.fromEntries(items.map((trade) => [trade.id, trade.fairMarketValueAsOn31Jan2018 ?? ''])));
    setSelectedTradeIds([]);
  };

  const handleRefresh = () => {
    if (!clientId || !financialYear) {
      return;
    }

    setLoading(true);
    fetchDeliveryTradesRequest(clientId, financialYear)
      .then((response) => {
        applyTrades(response.data);
        setError('');
      })
      .catch((requestError) => {
        console.error('Error fetching delivery trades', requestError);
        setError('Error fetching delivery trades.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  useEffect(() => {
    if (!clientId || !financialYear) {
      return;
    }

    setLoading(true);
    fetchDeliveryTradesRequest(clientId, financialYear)
      .then((response) => {
        applyTrades(response.data);
        setError('');
      })
      .catch((requestError) => {
        console.error('Error fetching delivery trades', requestError);
        setError('Error fetching delivery trades.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [clientId, financialYear]);

  const summary = useMemo(() => {
    const businessCount = trades.filter((trade) => (drafts[trade.id] || trade.tradeClassification) === 'BUSINESS').length;
    const investmentCount = trades.filter((trade) => (drafts[trade.id] || trade.tradeClassification) === 'INVESTMENT').length;
    return {
      total: trades.length,
      business: businessCount,
      investment: investmentCount,
    };
  }, [drafts, trades]);

  const selectedTradeIdSet = useMemo(() => new Set(selectedTradeIds), [selectedTradeIds]);
  const allVisibleSelected = trades.length > 0 && trades.every((trade) => selectedTradeIdSet.has(trade.id));

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedTradeIds(trades.map((trade) => trade.id));
      return;
    }
    setSelectedTradeIds([]);
  };

  const handleToggleTradeSelection = (tradeId) => {
    setSelectedTradeIds((currentTradeIds) => (
      currentTradeIds.includes(tradeId)
        ? currentTradeIds.filter((currentTradeId) => currentTradeId !== tradeId)
        : [...currentTradeIds, tradeId]
    ));
  };

  const handleSave = (tradeId) => {
    const tradeClassification = drafts[tradeId] || 'INVESTMENT';
    setSavingTradeId(tradeId);
    axios.put(`${apiUrl}/api/trades/delivery/${tradeId}/classification`, {
      tradeClassification,
      fairMarketValueAsOn31Jan2018: fmvDrafts[tradeId] === '' ? null : fmvDrafts[tradeId],
    })
      .then((response) => {
        setTrades((currentTrades) => currentTrades.map((trade) => (trade.id === tradeId ? response.data : trade)));
        setDrafts((currentDrafts) => ({
          ...currentDrafts,
          [tradeId]: response.data.tradeClassification || tradeClassification,
        }));
        setFmvDrafts((currentDrafts) => ({
          ...currentDrafts,
          [tradeId]: response.data.fairMarketValueAsOn31Jan2018 ?? '',
        }));
        setError('');
      })
      .catch((requestError) => {
        console.error('Error updating delivery trade classification', requestError);
        setError('Error updating delivery trade classification.');
      })
      .finally(() => {
        setSavingTradeId('');
      });
  };

  const handleBulkSave = () => {
    if (selectedTradeIds.length === 0) {
      return;
    }

    setBulkSaving(true);
    axios.put(`${apiUrl}/api/trades/delivery/classification/bulk`, {
      tradeIds: selectedTradeIds,
      tradeClassification: bulkClassification,
    })
      .then((response) => {
        const updatedTradesById = Object.fromEntries(response.data.map((trade) => [trade.id, trade]));
        setTrades((currentTrades) => currentTrades.map((trade) => updatedTradesById[trade.id] || trade));
        setDrafts((currentDrafts) => ({
          ...currentDrafts,
          ...Object.fromEntries(response.data.map((trade) => [trade.id, trade.tradeClassification || bulkClassification])),
        }));
        setSelectedTradeIds([]);
        setError('');
      })
      .catch((requestError) => {
        console.error('Error updating delivery trade classifications', requestError);
        setError('Error updating delivery trade classifications.');
      })
      .finally(() => {
        setBulkSaving(false);
      });
  };

  return (
    <div className="delivery-trades-page">
      <h2>Delivery Trade Classification</h2>
      <p className="financial-page-subtitle">Classify each delivery trade as business or investment. Intraday and F&amp;O remain business by rule and do not appear here.</p>

      <section className="delivery-summary-grid">
        <article className="delivery-summary-card">
          <span>Total Delivery Trades</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="delivery-summary-card">
          <span>Investment Classified</span>
          <strong>{summary.investment}</strong>
        </article>
        <article className="delivery-summary-card delivery-summary-card-accent">
          <span>Business Classified</span>
          <strong>{summary.business}</strong>
        </article>
      </section>

      <div className="financial-toolbar">
        <div className="delivery-bulk-toolbar">
          <span className="delivery-selection-summary">{selectedTradeIds.length} selected</span>
          <select value={bulkClassification} onChange={(event) => setBulkClassification(event.target.value)}>
            <option value="INVESTMENT">Investment</option>
            <option value="BUSINESS">Business</option>
          </select>
          <button
            className="financial-button"
            disabled={selectedTradeIds.length === 0 || bulkSaving}
            onClick={handleBulkSave}
          >
            {bulkSaving ? 'Applying...' : 'Apply to Selected'}
          </button>
          <button
            className="financial-button financial-button--secondary"
            disabled={selectedTradeIds.length === 0}
            onClick={() => setSelectedTradeIds([])}
          >
            Clear Selection
          </button>
        </div>
        <button className="financial-button financial-button--secondary" onClick={handleRefresh}>Refresh</button>
      </div>

      {loading && <p className="financial-message">Loading delivery trades...</p>}
      {error && <p className="financial-message financial-message--error">{error}</p>}

      {trades.length > 0 ? (
        <div className="table-container">
          <table className="delivery-trades-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(event) => handleSelectAll(event.target.checked)}
                    aria-label="Select all delivery trades"
                  />
                </th>
                <th>Scrip</th>
                <th>Buy Date</th>
                <th>Sell Date</th>
                <th>Qty</th>
                <th>Avg Buy Price</th>
                <th>Buy Value</th>
                <th>Avg Sell Price</th>
                <th>Sell Value</th>
                <th>31-Jan-2018 FMV</th>
                <th>Net Result</th>
                <th>Classification</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade) => (
                <tr key={trade.id} className={selectedTradeIdSet.has(trade.id) ? 'delivery-trades-table__row--selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTradeIdSet.has(trade.id)}
                      onChange={() => handleToggleTradeSelection(trade.id)}
                      aria-label={`Select ${trade.scripName || trade.isin || 'delivery trade'}`}
                    />
                  </td>
                  <td>{trade.scripName || trade.isin || '-'}</td>
                  <td>{formatDate(trade.buyDate)}</td>
                  <td>{formatDate(trade.sellDate)}</td>
                  <td>{trade.quantity}</td>
                  <td>₹ {formatAmount(trade.avgBuyPrice)}</td>
                  <td>₹ {formatAmount(trade.buyValue)}</td>
                  <td>₹ {formatAmount(trade.avgSellPrice)}</td>
                  <td>₹ {formatAmount(trade.sellValue)}</td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={fmvDrafts[trade.id] ?? ''}
                      onChange={(event) => setFmvDrafts((currentDrafts) => ({
                        ...currentDrafts,
                        [trade.id]: event.target.value,
                      }))}
                    />
                  </td>
                  <td>₹ {formatAmount(calculateNetAmount(trade))}</td>
                  <td>
                    <select
                      value={drafts[trade.id] || trade.tradeClassification || 'INVESTMENT'}
                      onChange={(event) => setDrafts((currentDrafts) => ({
                        ...currentDrafts,
                        [trade.id]: event.target.value,
                      }))}
                    >
                      <option value="INVESTMENT">Investment</option>
                      <option value="BUSINESS">Business</option>
                    </select>
                  </td>
                  <td>
                    <button
                      className="financial-button"
                      disabled={savingTradeId === trade.id || bulkSaving}
                      onClick={() => handleSave(trade.id)}
                    >
                      {savingTradeId === trade.id ? 'Saving...' : 'Save'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !loading ? (
        <p className="financial-message">No delivery trades found for the selected client and financial year.</p>
      ) : null}
    </div>
  );
};

export default DeliveryTradesPage;